import { randomID } from "../../../../utils/helpers";
import { useInputRules } from "../../../../composables/horizontalLabel";
import { computed, getCurrentInstance, ref, watch, watchEffect } from "vue";
// import MInput from "../MInput";
import { GroupOption, ItemOption, SelectLabelSymbol } from "./_types";
import { debounce, isArray, isObject, useDebounce, useEvent } from "vue-composable";
import axios from "../../../../utils/axios";
import { Key } from "ts-key-enum";
import { isString } from "@vue/shared";

const useMSearchSelect = (props: any, ctx: any) => {
  // const inputEl = ref<InstanceType<typeof MInput> | null>(null);
  const { proxy } = getCurrentInstance()!;
  const selectableItems = ref<Array<ItemOption>>([]);
  const debounceMs = computed(() => props.debounce);
  const originalValue = props.modelValue;

  //
  const open = () => (isOpen.value = true);
  const focus = () => ctx.inputEl.value?.focus();
  const selectedOption = ref<ItemOption | null>(null);
  const text = ref("");
  const isMousedown = ref(false); // Used to determine if input focus came from a click or a tab
  const focusedItem = ref<ItemOption | null>(null);
  const focusedItemId = ref(0);
  const searchedItems = ref<Array<GroupOption | ItemOption> | null>(null);
  const isOpen = ref(false);
  let lastSearchCancel: Function | undefined;
  const isFetching = ref(false);
  const searchFn = ref(doSearch);
  const emitBlur = useDebounce(() => ctx.emit("blur"), props.debounce);

  //
  const id = computed(() => (props.for ? props.for : randomID("select")));
  const menuId = computed(() => (props.for ? `${props.for}-menu` : randomID("select-menu")));
  const isInput = computed(() => Boolean(props.useInput !== false || props.searchUrl));
  const filteredItems = computed(() => {
    return searchedItems.value ?? [];
  });

  //
  async function doSearch(searchText: string) {
    if (props.minSearchValueLength && searchText.length < props.minSearchValueLength) {
      return;
    }

    try {
      lastSearchCancel?.();
      isFetching.value = true;
      let currentSearch: () => void = null;
      const cancelToken = new axios.CancelToken((cancel) => {
        currentSearch = lastSearchCancel = () => {
          cancel();
        };
      });

      const v = searchText;

      try {
        const results = await axios.get(props.searchUrl!, {
          cancelToken,
          params: {
            query: v,
          },
        });

        const data = isArray(results)
          ? results
          : isArray(results.data)
          ? results.data
          : results.data.results || [];

        ctx.emit("search-results", data);

        const items = props.itemMap ? data.map(props.itemMap) : data;

        if (currentSearch === lastSearchCancel) {
          searchedItems.value = props.searchFilter ? items.filter(props.searchFilter) : items;
          isOpen.value = true;
        }
      } catch (e) {
        currentSearch();
        if (currentSearch === lastSearchCancel) {
          lastSearchCancel = undefined;
          isFetching.value = false;
        }
      } finally {
        if (currentSearch === lastSearchCancel) {
          isFetching.value = false;
        }
      }
    } finally {
      //
    }
  }

  const { value, displayErrors, formTouched, touched } = useInputRules(
    props,
    () => {
      //focus
      ctx.inputEl.value?.focus();
    },
    [],
    true,
    () => props.requiredText || `Enter a value for '${props.label}'`,
    debounceMs,
    () => {
      ctx.inputEl.value.$el.scrollIntoView?.();
    },
  );

  function populateSelectableItems(items: Array<ItemOption | GroupOption>) {
    selectableItems.value = [];
    for (const item of items) {
      if ("items" in item) {
        for (const subItem of item.items) {
          selectableItems.value.push(subItem);
        }
      } else {
        selectableItems.value.push(item);
      }
    }
  }

  function onKeydown(e: KeyboardEvent) {
    if ((e.target as HTMLButtonElement).tagName === "BUTTON") {
      return;
    }
    let newOpen = isOpen.value;

    switch (e.code) {
      case Key.Tab:
        newOpen = false;
        break;
      case Key.ArrowDown:
        e.preventDefault();
        if (!newOpen) {
          newOpen = true;
        }
        if (focusedItemId.value < selectableItems.value.length - 1) {
          // go down, until hitting the end of the list, then cycle back to the top
          if (newOpen) {
            ++focusedItemId.value;
          }
          focusedItem.value = selectableItems.value[focusedItemId.value];
        } else {
          if (newOpen) {
            focusedItemId.value = 0;
            focusedItem.value = selectableItems.value[focusedItemId.value];
          }
        }
        break;
      case Key.ArrowUp: {
        e.preventDefault();
        if (!newOpen) {
          newOpen = true;
        }
        if (focusedItemId.value > 0) {
          // go up, until hitting the top of the list, then cycle back to the bottom
          if (newOpen) {
            --focusedItemId.value;
          }
          focusedItem.value = selectableItems.value[focusedItemId.value];
        } else {
          if (newOpen) {
            focusedItemId.value = selectableItems.value.length - 1;
            focusedItem.value = selectableItems.value[focusedItemId.value];
          }
        }
        break;
      }
      case "Space": {
        if (isInput.value) {
          return;
        }
        e.preventDefault();
        newOpen = !newOpen;
        break;
      }
      case Key.Enter:
        e.preventDefault();
        if (!newOpen) {
          newOpen = true;
        } else {
          if (focusedItem.value && !isFetching.value) {
            onSelect(focusedItem.value);
            newOpen = false;
          }
        }
        break;
      case Key.Escape: {
        e.preventDefault();
        if (isOpen.value) {
          e.stopPropagation();
        }
        newOpen = false;
        break;
      }
      case Key.Backspace:
      case Key.Delete: {
        if (isInput.value) return;

        if (props.clearable || !!props.searchUrl) {
          selectedOption.value = null;
          text.value = "";
          value.value = null;
        }
        break;
      }
    }

    isOpen.value = newOpen;
  }

  function onSelect(e: GroupOption | ItemOption) {
    if ("title" in e) return;

    ctx.emit("input-value", e);

    setOption(e);
    isOpen.value = false;
  }

  function onClick(e: MouseEvent) {
    if ((e.currentTarget as HTMLDivElement).contains(e.target as HTMLElement)) {
      isOpen.value = !isOpen.value;
    }
  }

  function getLabel(e: any) {
    if (!e) return "";
    return (
      e.label ||
      e.text ||
      e.displayName ||
      e.name ||
      e.description ||
      e ||
      e[SelectLabelSymbol] ||
      ""
    );
  }

  function setOption(e: ItemOption | any) {
    // if is the value, just ignore
    if (selectedOption.value && selectedOption.value.value === value.value && value.value === e) {
      if (e === null || e === undefined) {
        unsetOption();
      }
      return;
    }

    if (e == null) {
      unsetOption();
      return;
    }
    if (!isObject(e)) {
      unsetOption();
      return;
    }

    selectedOption.value = e as ItemOption;
    text.value = getLabel(e);
    value.value = e.value;
  }

  function unsetOption() {
    selectedOption.value = null;
    text.value = "";
    value.value = null;
  }

  function onBlur(e: FocusEvent) {
    if (e.relatedTarget) {
      const target = e.relatedTarget as HTMLElement;

      if (proxy.$el.contains(target)) return;
    }

    // reset text
    if (text.value !== selectedOption.value?.label) {
      onClear();
    }

    focusedItemId.value = 0;
    focusedItem.value = null;

    isOpen.value = false;
    emitBlur();

    if (formTouched) {
      setTimeout(() => {
        const t = (touched.value = touched.value || value.value !== originalValue);

        if (!proxy?.$.isUnmounted) {
          formTouched(t);
        }
      }, 50);
    }
  }

  function onClear() {
    if (isFetching.value) {
      lastSearchCancel?.();
      isFetching.value = false;
    }

    setOption(null);

    text.value = "";
    value.value = null;
    isOpen.value = false;
  }

  function selectText() {
    if (ctx.inputEl.value?.value) {
      ctx.inputEl.value?.select();
    }
  }

  //// WATCHERS & LISTENERS
  watch(filteredItems, (items) => {
    populateSelectableItems(items);

    // as we recompute the list reset the pointer to the top of the list
    if (selectableItems.value[0]) {
      focusedItemId.value = 0;
      focusedItem.value = selectableItems.value[0];
    } else {
      focusedItemId.value = 0;
      focusedItem.value = null;
    }
  });

  watch(isOpen, (open) => {
    // as we recompute the list reset the pointer to the top of the list
    if (open && focusedItem.value === null && selectableItems.value[0]) {
      focusedItemId.value = 0;
      focusedItem.value = selectableItems.value[0];
    }
  });

  watch(
    value,
    async (v, o) => {
      if (null === v && o) {
        onClear();
        return;
      }
      if (v && typeof v === "object") {
        if (v === selectedOption.value?.value) return;
        if (Array.isArray(v)) {
          // same object
          if (JSON.stringify(v) === JSON.stringify(o)) return;

          return;
        } else {
          const keys = Object.keys(v);
          if (keys.length == 2 && keys.includes("label") && keys.includes("value")) {
            selectedOption.value = v;
            text.value = getLabel(v);
            value.value = v.value;
          }
        }
      }

      // ENG-8731
      if (isString(v)) {
        selectedOption.value = { label: v, value: v };
        text.value = v;
        value.value = v;
      } else if (isObject(v) && !Array.isArray(v)) {
        const keys = Object.keys(v);
        if (keys.length == 2 && keys.includes("label") && keys.includes("value")) {
          // @ts-expect-error not the correct type
          selectedOption.value = v;
          text.value = getLabel(v);
          value.value = v.value;

          return;
        }
        selectedOption.value = {
          label: getLabel(v),
          value: v,
        };
        text.value = getLabel(v);
        value.value = v;
      }
    },
    { immediate: true, flush: "pre" },
  );

  watchEffect(() => {
    searchFn.value = debounceMs.value ? debounce(doSearch, +debounceMs.value) : doSearch;
  });

  watch(
    text,
    (v, p, onInvalidate) => {
      if (!v) {
        if (props.searchUrl) {
          searchedItems.value = [];
        }

        return;
      }
      if (!props.searchUrl) return;
      if (v === selectedOption.value?.label) return;

      searchFn.value(v);
      onInvalidate(() => {
        lastSearchCancel?.();
      });
    },
    {
      flush: "sync",
    },
  );

  useEvent(
    computed(() => ctx.inputEl.value?.$el),
    "blur",
    onBlur,
    { capture: true },
  );

  useEvent(window, "mouseup", () => {
    isMousedown.value = false;
  });

  const handleInput = () => {
    isOpen.value = true;
  };

  return {
    handleInput,
    debounceMs,
    displayErrors,
    doSearch,
    emitBlur,
    focus,
    focusedItem,
    focusedItemId,
    formTouched,
    filteredItems,
    getLabel,
    id,
    // inputEl,
    isFetching,
    isInput,
    isMousedown,
    isOpen,
    lastSearchCancel,
    menuId,
    onBlur,
    onClear,
    onClick,
    onKeydown,
    onSelect,
    open,
    originalValue,
    populateSelectableItems,
    proxy,
    searchFn,
    searchedItems,
    selectableItems,
    selectedOption,
    setOption,
    selectText,
    text,
    touched,
    unsetOption,
    value,
  };
};

export default useMSearchSelect;
